import type { CopyLang } from "./copyTypes";

export interface ThankYouLocation {
  assign(url: string): void;
  hostname(): string;
  delay(fn: () => void, ms: number): number;
  clearDelay(id: number): void;
}

export class BrowserThankYouLocation implements ThankYouLocation {
  assign(url: string): void {
    window.location.assign(url);
  }

  hostname(): string {
    return window.location.hostname;
  }

  delay(fn: () => void, ms: number): number {
    return window.setTimeout(fn, ms);
  }

  clearDelay(id: number): void {
    window.clearTimeout(id);
  }
}

export class ThankYouRedirect {
  static readonly DELAY_MS = 5_000;
  static readonly DEV_URL = "https://neelevatnl.sowmedia.dev/bedankt/";

  private static readonly URLS: Record<CopyLang, string> = {
    en: "https://www.neelevat.com/thank-you/",
    nl: "https://www.neelevat.nl/bedankt/",
    de: "https://www.neelevat.de/danke/",
    es: "https://www.neelevat.es/gracias/",
    fi: "https://www.neelevat.fi/kiitos/",
    fr: "https://www.neelevat.fr/merci/",
    hr: "https://www.neelevat.hr/hvala/",
    hu: "https://www.neelevat.hu/koszonom/",
    it: "https://www.neelevat.it/grazie/",
    sl: "https://www.neelevat.si/hvala/",
    sk: "https://www.neelevat.sk/dakujem/",
    zh: "https://www.neelevat.cn/xie-xie/",
  };

  private static readonly PRODUCTION_HOSTS: ReadonlySet<string> = new Set([
    "neelevat.com",
    "neelevat.nl",
    "neelevat.de",
    "neelevat.es",
    "neelevat.fi",
    "neelevat.fr",
    "neelevat.hr",
    "neelevat.hu",
    "neelevat.it",
    "neelevat.si",
    "neelevat.sk",
    "neelevat.cn",
    "neelevat.be",
  ]);

  private timer: number | null = null;

  constructor(private readonly location: ThankYouLocation = new BrowserThankYouLocation()) {}

  urlFor(language: CopyLang): string {
    if (this.isDevHost()) return ThankYouRedirect.DEV_URL;
    return ThankYouRedirect.URLS[language];
  }

  go(language: CopyLang): void {
    this.cancel();
    this.timer = this.location.delay(() => {
      this.timer = null;
      this.location.assign(this.urlFor(language));
    }, ThankYouRedirect.DELAY_MS);
  }

  cancel(): void {
    if (this.timer === null) return;
    this.location.clearDelay(this.timer);
    this.timer = null;
  }

  private isDevHost(): boolean {
    const host = this.location.hostname().toLowerCase().replace(/^www\./, "");
    return !ThankYouRedirect.PRODUCTION_HOSTS.has(host);
  }
}
